import React, { useEffect, useMemo, useState } from 'react';
import { Camera, FileText, Loader2, Mail, MessageCircle, Paperclip, Send, X } from 'lucide-react';
import { supabase } from '../../supabase';
import { buildApiUrl } from '../../utils/apiUrl';
import { btnPrimary, btnSecondary, cardCls, inputCls, labelCls, pageSubtitleCls, pageTitleCls } from '../../components/ui';
import { tomarCapturaPendiente } from '../../utils/capturaPendiente';

type Status = {
  type: 'success' | 'error';
  message: string;
};

type ServerStatus = {
  configured: boolean;
  apiBase: string | null;
  instance: string | null;
  signatureEnabled?: boolean;
  signature?: string | null;
};

type ContactoRegistrado = {
  user_id: string;
  nombre: string;
  telefonoLocal: string;
};

const SYSTEM_LOGO = `${process.env.PUBLIC_URL}/branding/login-logo-pina.png`;
const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || 'soporte@sistemamya.com';
const CODIGO_PAIS = '506';

function normalizePhone(phone: string) {
  return phone.replace(/\D/g, '');
}

// Quita el 506 si el numero ya lo trae (con o sin '+'), para mostrar solo el
// numero local de 8 digitos en el campo -- el prefijo se agrega aparte.
function soloNumeroLocal(phone: string) {
  const digitos = normalizePhone(phone);
  if (digitos.length > 8 && digitos.startsWith(CODIGO_PAIS)) {
    return digitos.slice(CODIGO_PAIS.length);
  }
  return digitos;
}

function fileToBase64(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      resolve(dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl);
    };
    reader.onerror = () => reject(reader.error || new Error('No se pudo leer el archivo.'));
    reader.readAsDataURL(file);
  });
}

function getMediaType(file: File) {
  if (file.type.startsWith('image')) return 'image';
  if (file.type.startsWith('video')) return 'video';
  return 'document';
}

async function getAuthHeaders() {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token || '';
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function readApiError(response: Response) {
  try {
    const data = await response.json();
    return data?.error || data?.message || `Error HTTP ${response.status}`;
  } catch {
    return `Error HTTP ${response.status}`;
  }
}

export function WhatsAppSenderPage({ empresaId }: { empresaId?: string | number }) {
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<Status | null>(null);
  const [serverStatus, setServerStatus] = useState<ServerStatus | null>(null);
  const [includeSignature, setIncludeSignature] = useState(true);
  const [esCaptura, setEsCaptura] = useState(false);
  const [contactos, setContactos] = useState<ContactoRegistrado[]>([]);
  const [contactoSelId, setContactoSelId] = useState('');

  const numeroLocal = useMemo(() => soloNumeroLocal(phone), [phone]);
  const cleanPhone = useMemo(
    () => (numeroLocal ? CODIGO_PAIS + numeroLocal : ''),
    [numeroLocal],
  );
  const canSend = numeroLocal.length === 8 && message.trim().length > 0 && !loading;

  // Si el botón flotante capturó la pantalla anterior, ya está lista acá —
  // se recoge una sola vez, el usuario la puede quitar si no la quiere.
  useEffect(() => {
    const captura = tomarCapturaPendiente();
    if (captura) { setFile(captura); setEsCaptura(true); }
  }, []);

  useEffect(() => {
    let active = true;
    async function loadContactos() {
      if (!empresaId) return;
      try {
        const headers = await getAuthHeaders();
        const response = await fetch(buildApiUrl('/aplicaciones/usuarios-empresa'), {
          headers: { ...headers, 'x-empresa-id': String(empresaId) },
        });
        if (!response.ok) return;
        const data = await response.json();
        const lista: ContactoRegistrado[] = (data.usuarios || [])
          .filter((u: any) => u.telefono && String(u.telefono).trim())
          .map((u: any) => ({
            user_id: u.user_id,
            nombre: u.nombre || u.email || u.user_id,
            telefonoLocal: soloNumeroLocal(String(u.telefono)),
          }))
          .filter((c: ContactoRegistrado) => c.telefonoLocal.length === 8);
        if (active) setContactos(lista);
      } catch {
        if (active) setContactos([]);
      }
    }
    loadContactos();
    return () => {
      active = false;
    };
  }, [empresaId]);

  useEffect(() => {
    let active = true;
    async function loadStatus() {
      try {
        const headers = await getAuthHeaders();
        const response = await fetch(buildApiUrl('/whatsapp/status'), {
          headers: {
            ...headers,
            ...(empresaId ? { 'X-Empresa-Id': String(empresaId) } : {}),
          },
        });
        if (!response.ok) return;
        const data = (await response.json()) as ServerStatus;
        if (active) {
          setServerStatus(data);
          setIncludeSignature(Boolean(data.signatureEnabled));
        }
      } catch {
        if (active) setServerStatus(null);
      }
    }
    loadStatus();
    return () => {
      active = false;
    };
  }, [empresaId]);

  async function sendMessage() {
    if (!canSend) return;

    setLoading(true);
    setStatus(null);

    try {
      const headers = await getAuthHeaders();
      const payload: Record<string, string | boolean> = {
        number: cleanPhone,
        text: message.trim(),
        includeSignature,
        ...(empresaId ? { empresa_id: String(empresaId) } : {}),
      };

      if (file) {
        payload.media = await fileToBase64(file);
        payload.mediatype = getMediaType(file);
        payload.mimetype = file.type || 'application/octet-stream';
        payload.fileName = file.name;
      }

      const response = await fetch(buildApiUrl('/whatsapp/send'), {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
          ...(empresaId ? { 'X-Empresa-Id': String(empresaId) } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(await readApiError(response));
      }

      setStatus({ type: 'success', message: 'Mensaje enviado correctamente.' });
      setMessage('');
      setFile(null);
      setEsCaptura(false);
    } catch (error) {
      setStatus({
        type: 'error',
        message: error instanceof Error ? error.message : 'Error desconocido',
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className={pageTitleCls}>WhatsApp</h1>
          <p className={pageSubtitleCls}>Envío individual de mensajes</p>
        </div>
        {serverStatus && (
          <div
            className="rounded-full border px-3 py-1 text-[11px] font-semibold"
            style={{
              borderColor: serverStatus.configured ? 'var(--tone-success-border)' : 'var(--tone-warn-border)',
              background: serverStatus.configured ? 'var(--tone-success-bg)' : 'var(--tone-warn-bg)',
              color: serverStatus.configured ? 'var(--tone-success-text)' : 'var(--tone-warn-text)',
            }}
          >
            {serverStatus.configured ? 'Servicio activo' : 'Sin configurar'}
          </div>
        )}
      </div>

      <section className={`${cardCls} overflow-hidden`}>
        <div className="grid min-h-[520px] lg:grid-cols-[0.85fr_1.15fr]">
          <aside
            className="relative flex flex-col justify-between overflow-hidden p-6"
            style={{
              background: 'linear-gradient(135deg, var(--green-muted), var(--green-soft))',
              borderRight: '1px solid var(--line)',
            }}
          >
            <img
              src={SYSTEM_LOGO}
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute left-1/2 top-1/2 h-64 w-64 -translate-x-1/2 -translate-y-1/2 object-contain opacity-20"
            />

            <div className="relative z-10">
              <div
                className="mb-5 flex h-11 w-11 items-center justify-center rounded-lg"
                style={{
                  background: 'rgba(255,255,255,.12)',
                  color: 'var(--emp-accent-txt)',
                  border: '1px solid rgba(255,255,255,.18)',
                }}
              >
                <MessageCircle size={24} />
              </div>
              <h2 className="text-xl font-semibold text-ink">Enviar mensaje</h2>
              <p className="mt-3 max-w-sm text-sm leading-6 text-ink-muted">
                Elige un número ya registrado o escribe el número local de Costa Rica (se agrega el +506 automáticamente). Los adjuntos se envían como imagen, video o documento según el tipo de archivo.
              </p>
            </div>

            <div className="relative z-10 mt-8">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-ink">Sistema MYA</p>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="mt-1 flex min-w-0 items-center gap-1.5 text-xs text-ink-muted transition-colors hover:text-ink"
                >
                  <Mail size={13} className="shrink-0" />
                  <span className="truncate">{SUPPORT_EMAIL}</span>
                </a>
              </div>
            </div>
          </aside>

          <div className="p-5 sm:p-6">
            <div className="space-y-5">
              {contactos.length > 0 && (
                <div>
                  <label className={labelCls} htmlFor="whatsapp-contacto">
                    Número registrado
                  </label>
                  <select
                    id="whatsapp-contacto"
                    value={contactoSelId}
                    onChange={(event) => {
                      const id = event.target.value;
                      setContactoSelId(id);
                      const c = contactos.find((x) => x.user_id === id);
                      if (c) setPhone(c.telefonoLocal);
                    }}
                    className={inputCls}
                  >
                    <option value="">— Escribir número manualmente —</option>
                    {contactos.map((c) => (
                      <option key={c.user_id} value={c.user_id}>
                        {c.nombre} · {c.telefonoLocal}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className={labelCls} htmlFor="whatsapp-phone">
                  Número (Costa Rica)
                </label>
                <div className="flex items-center gap-2">
                  <span
                    className="shrink-0 rounded border border-line bg-surface-overlay px-3 py-2 text-sm text-ink-muted"
                    aria-hidden="true"
                  >
                    +{CODIGO_PAIS}
                  </span>
                  <input
                    id="whatsapp-phone"
                    type="tel"
                    inputMode="numeric"
                    value={phone}
                    onChange={(event) => {
                      setPhone(soloNumeroLocal(event.target.value));
                      setContactoSelId('');
                    }}
                    placeholder="88888888"
                    maxLength={8}
                    className={inputCls}
                  />
                </div>
              </div>

              <div>
                <label className={labelCls} htmlFor="whatsapp-message">
                  Mensaje
                </label>
                <textarea
                  id="whatsapp-message"
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  rows={7}
                  className={`${inputCls} resize-none`}
                  placeholder="Escribe tu mensaje..."
                />
              </div>

              {serverStatus?.signatureEnabled && serverStatus.signature && (
                <label className="flex items-start gap-3 rounded border border-line bg-surface-overlay px-3 py-2">
                  <input
                    type="checkbox"
                    checked={includeSignature}
                    onChange={(event) => setIncludeSignature(event.target.checked)}
                    className="mt-1"
                  />
                  <span className="min-w-0 text-xs text-ink-muted">
                    <span className="block font-semibold text-ink">Agregar firma del sistema</span>
                    <span className="block truncate">{serverStatus.signature}</span>
                  </span>
                </label>
              )}

              <div>
                <span className={labelCls}>Archivo adjunto</span>

                {file ? (
                  <div className="flex items-center justify-between gap-3 rounded border border-line bg-surface-overlay px-3 py-2">
                    <div className="flex min-w-0 items-center gap-3">
                      {esCaptura
                        ? <Camera className="h-5 w-5 shrink-0" style={{ color: 'var(--emp-accent-txt)' }} aria-hidden="true" />
                        : <FileText className="h-5 w-5 shrink-0 text-ink-faint" aria-hidden="true" />}
                      <span className="truncate text-sm text-ink-muted">
                        {esCaptura ? 'Captura de la pantalla anterior' : file.name}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setFile(null); setEsCaptura(false); }}
                      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-ink-faint transition-colors hover:bg-surface-raised hover:text-ink"
                      aria-label="Quitar archivo"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <label className="flex cursor-pointer items-center justify-center rounded border border-dashed border-line bg-surface-overlay px-4 py-7 transition-colors hover:border-[var(--emp-accent)]">
                    <input
                      type="file"
                      onChange={(event) => { setFile(event.target.files?.[0] || null); setEsCaptura(false); }}
                      className="hidden"
                    />
                    <span className="flex items-center gap-3 text-sm text-ink-muted">
                      <Paperclip size={18} className="text-ink-faint" />
                      Seleccionar imagen o documento
                    </span>
                  </label>
                )}
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={sendMessage}
                  disabled={!canSend}
                  className={`${btnPrimary} inline-flex items-center justify-center gap-2 sm:min-w-44`}
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  {loading ? 'Enviando...' : 'Enviar'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPhone('');
                    setContactoSelId('');
                    setMessage('');
                    setFile(null);
                    setEsCaptura(false);
                    setStatus(null);
                  }}
                  className={btnSecondary}
                  disabled={loading}
                >
                  Limpiar
                </button>
              </div>

              {status && (
                <div
                  className="rounded border px-3 py-2 text-sm"
                  style={{
                    borderColor: status.type === 'success' ? 'var(--tone-success-border)' : '#7f1d1d',
                    background: status.type === 'success' ? 'var(--tone-success-bg)' : 'rgba(127,29,29,.25)',
                    color: status.type === 'success' ? 'var(--tone-success-text)' : '#fca5a5',
                  }}
                  role="status"
                >
                  {status.message}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
